import { useState, useEffect, memo, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Plus, Search, Edit, Trash2, Eye, Loader2, RefreshCw, ChevronDown } from "lucide-react";
import { ImportExportActions, ImportMode, generateBachelorDegreeTemplate } from "./ImportExportUtils";
import { BachelorDegreeModal } from "./modals/BachelorDegreeModal";
import { notify } from "@/lib/utils/toast";
import { bachelorDegreesApi, type BachelorDegree, type BachelorDegreeFilters } from "../lib/api";
import { useAuthStore, PERMISSIONS } from "../lib/stores/auth";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { cn } from "./ui/utils";
import { BachelorDegreeDetail } from "./modals/BachelorDegreeDetail";
import { format, parse } from "date-fns";
import { StatusBadge } from "./StatusBadge";
import { PaginationIndex } from "./PaginationIndex";
import { majorCodesWithName, statusList } from "./validations/shared";
import { FilterYear } from "./share/Utils";
import { AccessDenied } from "./AccessDenied";
import { NgocInput } from "./ui/ngocInput";
import { validateBachelorDegreeImportDataFormat, validateBachelorDegreeImportDataInDB } from "./validations/bachelorDegree";
import { vi } from "date-fns/locale";
import { SmoothLoadingWrapper } from "./animate/SmoothLoadingWrapper";

export default function BachelorDegreeManagement() {
	const { hasPermission } = useAuthStore();
	const [degreeInformation, setDegreeInformation] = useState<BachelorDegree | null>(null);
	const [degrees, setDegrees] = useState<BachelorDegree[]>([]);
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState("");
	const [requestTypeFilter, setRequestTypeFilter] = useState("");
	const [trainingModeFilter, setTrainingModeFilter] = useState("");
	const [majorFilter, setMajorFilter] = useState("");
	const [statusFilter, setStatusFilter] = useState("");
	const [yearFromFilter, setYearFromFilter] = useState("");
	const [yearToFilter, setYearToFilter] = useState("");
	const [deleteItemId, setDeleteItemId] = useState<string | null>(null);
	const [modalMode, setModalMode] = useState<"create" | "edit">("create");
	const [searchMajorName, setSearchMajorName] = useState("");
	const [isFiltering, setIsFiltering] = useState(false);
	const [autoFilters, setAutoFilters] = useState<BachelorDegreeFilters>({});

	// Pagination
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [totalCount, setTotalCount] = useState(0);
	const [itemsPerPage, setItemsPerPage] = useState(10);

	// Modal states
	const [isHandleModalOpen, setHandleModalOpen] = useState(false);
	const [editingDegree, setEditingDegree] = useState<BachelorDegree | null>(null);

	// Loading states for individual operations
	const [isUpdating, setIsUpdating] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	// Permission checks
	const canView = hasPermission(PERMISSIONS.bachelorDegrees.permissions.view.key);
	const canCreate = hasPermission(PERMISSIONS.bachelorDegrees.permissions.create.key);
	const canUpdate = hasPermission(PERMISSIONS.bachelorDegrees.permissions.update.key);
	const canDelete = hasPermission(PERMISSIONS.bachelorDegrees.permissions.delete.key);
	const canImport = hasPermission(PERMISSIONS.bachelorDegrees.permissions.import.key);
	const canExport = hasPermission(PERMISSIONS.bachelorDegrees.permissions.create.key);

	if (!canView) return <AccessDenied name='danh sách văn bằng đại học' />;

	const [majors, setMajors] = useState<string[]>([]);

	const majorOptions: { value: string; label: string }[] = [];
	majorCodesWithName.forEach((mjcwn: { code: string; name: string }) => {
		const search = majorOptions.find((mjs) => mjs.label === mjcwn.name);
		if (!search) {
			majorOptions.push({
				value: mjcwn.name,
				label: mjcwn.name,
			});
		}
	});

	const toggleSelectMajor = (val: string) => {
		const displayData = majors.includes(val) ? majors.filter((v) => v !== val) : [...majors, val];
		setMajors(displayData);
		handleFilterChange(setMajorFilter, displayData);
	};

	const [classifies, setClassifies] = useState<string[]>([]);

	const classifyOptions = [
		{ value: "Xuất sắc", label: "Xuất sắc" },
		{ value: "Giỏi", label: "Giỏi" },
		{ value: "Khá", label: "Khá" },
		{ value: "Trung bình", label: "Trung bình" },
		{ value: "Yếu", label: "Yếu" },
		{ value: "Kém", label: "Kém" },
		{ value: "Trung bình khá", label: "Trung bình khá" },
	];

	const toggleSelectClassify = (val: string) => {
		const displayData = classifies.includes(val) ? classifies.filter((v) => v !== val) : [...classifies, val];
		setClassifies(displayData);
		// handleFilterChange(setMajorFilter, displayData);
	};

	const getFullRecord = async (filter: BachelorDegreeFilters = {}) => {
		const result = await bachelorDegreesApi.getAll({
			...filter,
			page: 1,
			limit: 999999
		});
		return result.data.data || [];
	};

	const fetchList = useCallback(async (customFilters?: BachelorDegreeFilters) => {
		let finalFilter = customFilters || autoFilters;
		try {
			setLoading(true);

			// Check if API is available
			if (!bachelorDegreesApi || typeof bachelorDegreesApi.getAll !== "function") {
				throw new Error("API service not available");
			}

			const result = await bachelorDegreesApi.getAll(finalFilter);

			if (result && result.success && result.data) {
				setDegrees(result.data.data || []);
				setTotalCount(result.data.total || 0);
				setTotalPages(result.data.totalPages || 1);
			} else {
				console.warn("API returned unsuccessful result:", result);
				// Set empty data as fallback
				setDegrees([]);
				setTotalCount(0);
				setTotalPages(1);
			}
		} catch (error) {
			console.error("Error fetching degrees:", error);
			// Set empty data as fallback
			setDegrees([]);
			setTotalCount(0);
			setTotalPages(1);
		} finally {
			setLoading(false);
		}
	}, [autoFilters]);

	useEffect(() => {
		fetchList({
			...autoFilters,
			page: currentPage,
		});
	}, [currentPage]);

	useEffect(() => {
		if (currentPage !== 1) {
			setCurrentPage(1);
		}
	}, [itemsPerPage]);

	// Reset to first page when filters change
	const handleFilterChange = (setter: Function, value: any) => {
		setter(value);
		setCurrentPage(1);
	};

	// Handle add new degree
	const handleAdd = async (newDegree: Omit<BachelorDegree, "id" | "createdAt" | "updatedAt">) => {
		if (!canCreate) {
			notify.error("Bạn không có quyền tạo văn bằng");
			return;
		}

		try {
			const result = await bachelorDegreesApi.create(newDegree);

			if (result.success) {
				await fetchList();
				notify.success("Thêm văn bằng thành công");
				setHandleModalOpen(false);
			} else {
				throw new Error(result.message || "Lỗi khi thêm văn bằng");
			}
		} catch (error) {
			console.error("Error creating degree:", error);
			const message = error instanceof Error ? error.message : "Lỗi khi thêm văn bằng";
			notify.error(message);
		} finally {
		}
	};

	// Handle edit degree
	const handleEdit = (degree: BachelorDegree) => {
		if (!canUpdate) {
			notify.error("Bạn không có quyền chỉnh sửa văn bằng");
			return;
		}
		setEditingDegree(degree);
	};

	// Handle update degree
	const handleUpdate = async (updatedDegree: BachelorDegree) => {
		if (!canUpdate) {
			notify.error("Bạn không có quyền cập nhật văn bằng");
			return;
		}

		try {
			setIsUpdating(true);
			const result = await bachelorDegreesApi.update(updatedDegree.id, updatedDegree);

			if (result.success) {
				await fetchList();
				notify.success("Cập nhật văn bằng thành công");
			} else {
				throw new Error("Lỗi khi cập nhật văn bằng");
			}
		} catch (error) {
			console.error("Error updating degree:", error);
		} finally {
			setIsUpdating(false);
		}
	};

	// Handle delete degree
	const handleDelete = async () => {
		if (!canDelete) {
			notify.error("Bạn không có quyền xóa văn bằng");
			return;
		}
		if (deleteItemId == null) {
			notify.error("Chưa chọn văn bằng cần xóa");
			return;
		}

		try {
			setIsDeleting(true);
			const result = await bachelorDegreesApi.delete(deleteItemId);

			if (result.success) {
				await fetchList();
				notify.success("Xóa văn bằng thành công");
			} else {
				throw new Error("Lỗi khi xóa văn bằng");
			}
		} catch (error) {
			console.error("Error deleting degree:", error);
		} finally {
			setIsDeleting(false);
		}
	};

	const handleImport = async (data: any[]) => {
		if (!canImport) {
			notify.error("Bạn không có quyền import văn bằng");
			throw new Error("Không có quyền import");
		}

		try {
			const importedDataFromExcel: any = data.map((row) => ({
				majorCode: row["Mã Ngành (*)"],
				major: row["Ngành (*)"],
				cccd: row["CCCD (*)"],
				studentId: row["Mã sinh viên (*)"],
				studentName: row["Họ và tên (*)"],
				dateOfBirth: row["Ngày sinh (*)"],
				placeOfBirth: row["Nơi sinh"],
				gender: row["Giới tính (*)"],
				ethnicCode: row["Mã Dân tộc"],
				ethnicity: row["Dân tộc"],
				nationalityCode: row["Mã quốc tịch"],
				nationality: row["Quốc tịch"],
				graduationYear: row["Năm tốt nghiệp (*)"],
				classification: row["Xếp loại tốt nghiệp"],
				classificationId: row["Xếp loại tốt nghiệp ID (*)"],
				trainingMode: row["Hình thức đào tạo (*)"],
				degreeNumber: row["Số hiệu bằng (cơ sở) (*)"],
				registrationNumber: row["Số vào sổ cấp bằng (*)"],
				signedBy: row["Người ký (*)"],
				signerTitle: row["Chức danh người ký (*)"],
				signDate: row["Ngày ký (*)"],
				course: row["Khoá học (*)"],
				recognitionDecisionNumber: row["Quyết định công nhận số (*)"],
				requestTypeId: row["Loại yêu cầu ID (*)"],
				requestType: row["Loại yêu cầu (*)"],
				notes: row["Ghi chú"],
				status: row.status, // Server will set default status
			}));

			//= Gọi hàm kiểm tra từng hàng dữ liệu có khớp với yêu cầu ràng buộc không
			let validatedImportData = validateBachelorDegreeImportDataFormat(importedDataFromExcel);
			let hasErrors = validatedImportData.some((x) => x.errors.length !== 0);
			if (hasErrors) {
				return validatedImportData;
			}

			validatedImportData = await validateBachelorDegreeImportDataInDB(validatedImportData);
			// validatedImportData.forEach((row) => {
			// 	if (row.errors.length > 0) {
			// 		console.log("Validation errors for row:", row, row.errors);
			// 	}
			// });
			hasErrors = validatedImportData.some((x) => x.errors.length !== 0);

			if (hasErrors) {
				return validatedImportData;
			}

			//= Nếu không lỗi thì format lại dữ liệu rồi gửi dữ liệu import đến back-end
			const formattedImportData = validatedImportData.map((x) => {
				return { ...x, dateOfBirth: format(parse(x.dateOfBirth, "dd/MM/yyyy", new Date()), "yyyy-MM-dd", { locale: vi }) };
			});

			const result = await bachelorDegreesApi.bulkImport(formattedImportData);

			await fetchList();
			return undefined
		} catch (error) {
			console.error("Error importing degrees:", error);
		}
	};

	// Handle export
	const handleExport = useCallback(async () => {
		if (!canExport) {
			notify.error("Bạn không có quyền export văn bằng");
			return [];
		}

		const exportData = await getFullRecord(isFiltering ? autoFilters : {});

		return exportData.map((row) => ({
			"Mã Ngành (*)": row.majorCode,
			"Ngành (*)": row.major,
			"CCCD (*)": row.cccd,
			"Mã sinh viên (*)": row.studentId,
			"Họ và tên (*)": row.studentName,
			"Ngày sinh (*)": row.dateOfBirth ? format(new Date(row.dateOfBirth), "dd/MM/yyyy") : "",
			"Nơi sinh": row.placeOfBirth,
			"Giới tính (*)": row.gender,
			"Mã Dân tộc": row.ethnicCode,
			"Dân tộc": row.ethnicity,
			"Mã quốc tịch": row.nationalityCode,
			"Quốc tịch": row.nationality,
			"Năm tốt nghiệp (*)": row.graduationYear,
			"Xếp loại tốt nghiệp": row.classification,
			"Xếp loại tốt nghiệp ID (*)": row.classificationId,
			"Hình thức đào tạo (*)": row.trainingMode,
			"Số hiệu bằng (cơ sở) (*)": row.degreeNumber,
			"Số vào sổ cấp bằng (*)": row.registrationNumber,
			"Người ký (*)": row.signedBy,
			"Chức danh người ký (*)": row.signerTitle,
			"Ngày ký (*)": row.signDate ? format(new Date(row.signDate), "dd/MM/yyyy") : "",
			"Khoá học (*)": row.course,
			"Quyết định công nhận số (*)": row.recognitionDecisionNumber,
			"Loại yêu cầu ID (*)": row.requestTypeId,
			"Loại yêu cầu (*)": row.requestType,
			"Ghi chú": row.notes,
		}));
	}, [autoFilters, isFiltering, canExport]);

	const handleExportErrors = (data: any[]) => {
		// Chuyển đổi dữ liệu có lỗi sang định dạng phù hợp để xuất file, không nên biến đổi giá trị
		return data.map((row) => {
			return {
				"Mã Ngành (*)": row.majorCode,
				"Ngành (*)": row.major,
				"CCCD (*)": row.cccd,
				"Mã sinh viên (*)": row.studentId,
				"Họ và tên (*)": row.studentName,
				"Ngày sinh (*)": row.dateOfBirth,
				"Nơi sinh": row.placeOfBirth,
				"Giới tính (*)": row.gender,
				"Mã Dân tộc": row.ethnicCode,
				"Dân tộc": row.ethnicity,
				"Mã quốc tịch": row.nationalityCode,
				"Quốc tịch": row.nationality,
				"Năm tốt nghiệp (*)": row.graduationYear,
				"Xếp loại tốt nghiệp": row.classification,
				"Xếp loại tốt nghiệp ID (*)": row.classificationId,
				"Hình thức đào tạo (*)": row.trainingMode,
				"Số hiệu bằng (cơ sở) (*)": row.degreeNumber,
				"Số vào sổ cấp bằng (*)": row.registrationNumber,
				"Người ký (*)": row.signedBy,
				"Chức danh người ký (*)": row.signerTitle,
				"Ngày ký (*)": row.signDate,
				"Khoá học (*)": row.course,
				"Quyết định công nhận số (*)": row.recognitionDecisionNumber,
				"Loại yêu cầu ID (*)": row.requestTypeId,
				"Loại yêu cầu (*)": row.requestType,
				"Ghi chú": row.notes,
				Lỗi: row.errors,
			}
		});
	};

	const clearFilters = () => {
		setSearchTerm("");
		setTrainingModeFilter("all");
		setRequestTypeFilter("all");
		setMajorFilter("all");
		setYearFromFilter("");
		setYearToFilter("");
		setMajors([]);
		setIsFiltering(false);
		setClassifies([]);
		setStatusFilter("");
		setCurrentPage(1);
		setItemsPerPage(10);
		setAutoFilters({});
		fetchList({});
	};

	const startFilters = () => {
		setIsFiltering(true);
		fetchList();
	};


	useEffect(() => {
		setAutoFilters({
			search: searchTerm.trim().toLocaleLowerCase(),
			requestType: requestTypeFilter,
			major: majorFilter,
			yearFrom: yearFromFilter,
			yearTo: yearToFilter,
			classification: classifies.join(","),
			trainingMode: trainingModeFilter,
			status: statusFilter,
			page: currentPage,
			limit: itemsPerPage,
		})
	}, [itemsPerPage, searchTerm, requestTypeFilter, majorFilter, yearFromFilter, yearToFilter, currentPage, majors, classifies, trainingModeFilter, statusFilter]);

	return (
		<div className='space-y-6'>
			<div className='flex items-center justify-between'>
				<div>
					<h2 className='text-2xl mb-2'>Quản Lý Văn Bằng Đại học</h2>
				</div>
				<div className='flex gap-2'>
					<Button
						variant='outline'
						onClick={() => fetchList()}
						disabled={loading}
					>
						<RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
						Làm mới
					</Button>
					{canCreate && (
						<Button
							variant={"add"}
							onClick={() => {
								setModalMode("create");
								setHandleModalOpen(true);
							}}
						>
							<Plus className='w-4 h-4' />
							Thêm văn bằng
						</Button>
					)}
					{(canImport || canExport) && (
						<ImportExportActions
							onImport={canImport ? handleImport : async (_: any[], mode: ImportMode) => []}
							onExport={canExport ? handleExport : async () => []}
							templateData={generateBachelorDegreeTemplate()}
							onExportError={handleExportErrors}
							entityName='Văn bằng Đại học'
							sampleFileName={`DSVBĐH_${format(new Date(), "ddMMyyyy")}`}
							sampleErrorFileName={`DSVBĐH_Errors_${format(new Date(), "ddMMyyyy")}`}
							validHeaderImport={[
								"Mã Ngành (*)",
								"Ngành (*)",
								"CCCD (*)",
								"Mã sinh viên (*)",
								"Họ và tên (*)",
								"Ngày sinh (*)",
								"Nơi sinh",
								"Giới tính (*)",
								"Mã Dân tộc",
								"Dân tộc",
								"Mã quốc tịch",
								"Quốc tịch",
								"Năm tốt nghiệp (*)",
								"Xếp loại tốt nghiệp",
								"Xếp loại tốt nghiệp ID (*)",
								"Hình thức đào tạo (*)",
								"Số hiệu bằng (cơ sở) (*)",
								"Số vào sổ cấp bằng (*)",
								"Người ký (*)",
								"Chức danh người ký (*)",
								"Ngày ký (*)",
								"Khoá học (*)",
								"Quyết định công nhận số (*)",
								"Loại yêu cầu ID (*)",
								"Loại yêu cầu (*)",
								"Ghi chú",
							]}
						/>
					)}
				</div>
			</div>

			<Card>
				<CardHeader>
					<CardTitle>Danh Sách Văn Bằng Đại Học</CardTitle>
					<div className='flex flex-wrap items-center gap-4 mt-4'>
						<div className='relative'>
							<Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
							<NgocInput placeholder='Tìm kiếm theo mã SV, tên, Số hiệu văn bằng, CCCD' value={searchTerm} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleFilterChange(setSearchTerm, e.target.value)} maxLength={100} className="pl-8 min-w-96" />
						</div>
						<Select value={requestTypeFilter} onValueChange={(value) => handleFilterChange(setRequestTypeFilter, value)}>
							<SelectTrigger className='w-48'>
								<SelectValue placeholder='Loại yêu cầu' />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value='all'>Tất cả</SelectItem>
								<SelectItem value='Cấp mới'>Cấp mới</SelectItem>
								<SelectItem value='Chỉnh sửa'>Chỉnh sửa</SelectItem>
								<SelectItem value='Huỷ bằng'>Huỷ bằng</SelectItem>
							</SelectContent>
						</Select>

						<Select value={trainingModeFilter} onValueChange={(value) => handleFilterChange(setTrainingModeFilter, value)}>
							<SelectTrigger className='w-48'>
								<SelectValue placeholder='Hình thức đào tạo' />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value='all'>Tất cả</SelectItem>
								<SelectItem value='Chính quy'>Chính quy</SelectItem>
								<SelectItem value='Liên thông'>Liên thông</SelectItem>
								<SelectItem value='Từ xa'>Từ xa</SelectItem>
								<SelectItem value='Tại chức'>Tại chức</SelectItem>
							</SelectContent>
						</Select>

						<Select value={statusFilter} onValueChange={(value) => handleFilterChange(setStatusFilter, value)}>
							<SelectTrigger className='w-48'>
								<SelectValue placeholder='Trạng thái' />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value='all'>Tất cả trạng thái</SelectItem>
								{statusList.map((st) => {
									return (
										<SelectItem key={st.code} value={st.name}>
											{st.name}
										</SelectItem>
									);
								})}
							</SelectContent>
						</Select>

						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant='outline' className={cn("w-fit justify-between text-slate-700 font-normal")}>
									{majors.length === 1 ? majorOptions.find((x) => x.value === majors[0])?.label : majors.length > 1 ? `Đã chọn ${majors.length} nghành học` : "Ngành học"}
									<ChevronDown />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent>
								<div className='flex gap-1'>
									<input
										value={searchMajorName}
										onInput={(e: any) => {
											setSearchMajorName(e.target.value);
										}}
										type='text'
										className='border w-full px-1'
										placeholder='Tìm kiếm...'
										onKeyDown={(e) => {
											e.stopPropagation();
										}}
									/>
									<button
										onClick={() => {
											setSearchMajorName("");
										}}
										type='button'
										className='cursor-pointer text-sm'
									>
										<RefreshCw size={"1rem"} />
									</button>
								</div>
								<div className='h-60 overflow-y-scroll'>
									{majorOptions
										.filter((mj) => mj.label.toLocaleLowerCase().includes(searchMajorName.trim().toLowerCase()))
										.map((option) => (
											<DropdownMenuCheckboxItem
												key={option.value}
												checked={majors.includes(option.value)}
												onCheckedChange={() => toggleSelectMajor(option.value)}
												className='cursor-pointer'
											>
												{option.label}
											</DropdownMenuCheckboxItem>
										))}
								</div>
							</DropdownMenuContent>
						</DropdownMenu>

						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant='outline' className={cn("w-52 justify-between text-slate-700 font-normal")}>
									{classifies.length === 1 ? classifyOptions.find((x) => x.value === classifies[0])?.label : classifies.length > 1 ? `Đã chọn ${classifies.length} loại tốt nghiệp` : "Loại tốt nghiệp"}
									<ChevronDown />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent>
								{classifyOptions.map((option) => (
									<DropdownMenuCheckboxItem
										key={option.value}
										checked={classifies.includes(option.value)}
										onCheckedChange={() => toggleSelectClassify(option.value)}
										className='capitalize cursor-pointer'
									>
										{option.label}
									</DropdownMenuCheckboxItem>
								))}
							</DropdownMenuContent>
						</DropdownMenu>

						<div className='flex items-center gap-2'>
							<FilterYear query={yearFromFilter} placeHolder='Từ năm' onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFilterChange(setYearFromFilter, e.target.value)} />
							<span className='text-muted-foreground'>-</span>
							<FilterYear query={yearToFilter} placeHolder='Đến năm' onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFilterChange(setYearToFilter, e.target.value)} />
						</div>
						{/* {((requestTypeFilter && requestTypeFilter !== "all") || (majorFilter && majorFilter !== "all") || yearFromFilter || yearToFilter) && (
						)} */}
						<Button size={"sm"} variant='edit' onClick={() => { startFilters() }}>
							Lọc
						</Button>
						<Button size={"sm"} variant='outline' onClick={clearFilters}>
							Đặt lại
						</Button>
					</div>
				</CardHeader>
				<CardContent>
					{/* Table with horizontal scroll */}
					<Select value={itemsPerPage.toString()} onValueChange={(value) => {
						setItemsPerPage(Number(value));
						fetchList({
							...autoFilters,
							limit: Number(value),
							page: 1
						});
					}}>
						<SelectTrigger className='w-20'>
							<SelectValue placeholder='Hình thức đào tạo' />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value='10'>10</SelectItem>
							<SelectItem value='15'>15</SelectItem>
							<SelectItem value='20'>20</SelectItem>
							<SelectItem value='50'>50</SelectItem>
						</SelectContent>
					</Select>
					<div className='relative rounded-md border mt-2'>
						<SmoothLoadingWrapper loading={loading} >
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead className='w-[120px]'>Mã sinh viên</TableHead>
										<TableHead className='w-[150px]'>Họ tên</TableHead>
										<TableHead className='w-[80px]'>Năm TN</TableHead>
										<TableHead className='w-[180px]'>Ngành</TableHead>
										<TableHead className='w-[100px]'>Xếp loại</TableHead>
										<TableHead className='w-[150px]'>Số hiệu bằng</TableHead>
										<TableHead className='w-[150px]'>Trạng thái</TableHead>
										<TableHead className='w-[100px]'>Hình thức</TableHead>
										<TableHead className='w-[140px]'>Loại yêu cầu</TableHead>
										<TableHead className='w-[150px]'>Hành động</TableHead>
										{/* <TableHead className='w-[130px]'>CCCD</TableHead> */}
										{/* <TableHead className='w-[100px]'>Mã ngành</TableHead> */}
										{/* <TableHead className='w-[100px]'>Khoá học</TableHead> */}
										{/* <TableHead className='w-[120px]'>Thao tác</TableHead> */}
									</TableRow>
								</TableHeader>
								<TableBody>
									{degrees.length === 0 ? (
										<TableRow>
											<TableCell colSpan={11} className='text-center py-8 text-muted-foreground'>
												{searchTerm || requestTypeFilter || majorFilter || yearFromFilter || yearToFilter ? "Không tìm thấy kết quả phù hợp" : "Không có dữ liệu"}
											</TableCell>
										</TableRow>
									) : (
										degrees.map((degree) => (
											<TableRow key={degree.id}>
												<TableCell className="whitespace-normal text-sm" style={{ wordBreak: 'break-word' }}>{degree.studentId}</TableCell>
												<TableCell className='whitespace-normal text-sm' style={{ wordBreak: 'break-word', textTransform: "capitalize" }}>
													{degree.studentName}
												</TableCell>
												<TableCell className="whitespace-normal text-sm" style={{ wordBreak: 'break-word' }}>{degree.graduationYear}</TableCell>
												<TableCell className="whitespace-normal text-sm" style={{ wordBreak: 'break-word' }}>{degree.major}</TableCell>
												<TableCell>
													<GraduateClassificationBadge name={degree.classification} />
												</TableCell>
												<TableCell className="whitespace-normal text-sm" style={{ wordBreak: 'break-word' }}>{degree.degreeNumber}</TableCell>

												{/* <TableCell className="whitespace-normal text-sm" style={{wordBreak: 'break-word'}}>{degree.cccd}</TableCell>
												<TableCell className="whitespace-normal text-sm" style={{wordBreak: 'break-word'}}>{degree.majorCode}</TableCell>
												<TableCell className='font-medium'>{degree.course}</TableCell> */}

												<TableCell>
													<StatusBadge name={degree.status} />
												</TableCell>
												<TableCell>
													<Badge variant='outline' className='text-xs'>
														{degree.trainingMode}
													</Badge>
												</TableCell>
												<TableCell>
													<Badge variant='outline' className='text-xs'>
														{degree.requestType}
													</Badge>
												</TableCell>
												<TableCell>
													<div className='flex items-center gap-1'>
														<Button
															onClick={() => {
																setDegreeInformation(degree);
															}}
															variant='ghost'
															className='h-8 w-8 p-0'
														>
															<Eye className='w-4 h-4' />
														</Button>
														{canUpdate && (
															<Button
																variant='ghost'
																onClick={() => {
																	setModalMode("edit");
																	handleEdit(degree);
																	setHandleModalOpen(true);
																}}
																className='h-8 w-8 p-0 text-yellow-600 hover:text-yellow-600'
																disabled={isUpdating}
															>
																<Edit className='w-4 h-4' />
															</Button>
														)}
														{canDelete && (
															<Button variant='ghost' onClick={() => setDeleteItemId(degree.id)} className='h-8 w-8 p-0 text-destructive hover:text-destructive' disabled={isDeleting}>
																{isDeleting ? <Loader2 className='w-4 h-4 animate-spin' /> : <Trash2 className='w-4 h-4' />}
															</Button>
														)}
													</div>
												</TableCell>
											</TableRow>
										))
									)}
								</TableBody>
							</Table>
						</SmoothLoadingWrapper>
					</div>

					{/* Pagination */}
					{!loading && totalPages > 1 && <PaginationIndex setCurrentPage={setCurrentPage} currentPage={currentPage} itemsPerPage={itemsPerPage} totalCount={totalCount} totalPages={totalPages} />}
				</CardContent>
			</Card>

			{/* Handle Modal */}
			<BachelorDegreeModal isOpen={isHandleModalOpen} onClose={() => setHandleModalOpen(false)} onAdd={handleAdd} editData={editingDegree} onUpdate={handleUpdate} mode={modalMode} />

			<BachelorDegreeDetail data={degreeInformation} setDegreeInformation={setDegreeInformation} />

			<AlertDialog open={deleteItemId !== null} onOpenChange={() => setDeleteItemId(null)}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Xác nhận xóa</AlertDialogTitle>
						<AlertDialogDescription>Bạn có chắc chắn muốn xóa? Hành động này không thể hoàn tác.</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Hủy</AlertDialogCancel>
						<AlertDialogAction onClick={handleDelete} className='bg-destructive text-destructive-foreground hover:bg-destructive/90'>
							Xóa
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}

const classificationMapping: Record<string, "destructive" | "add" | "outline" | "default" | "secondary" | null | undefined> = {
	"Xuất sắc": "destructive",
	Giỏi: "add",
	Khá: "outline",
	"Trung bình": "outline",
	Yếu: "outline",
	Kém: "outline",
	"Trung bình khá": "outline",
};
const GraduateClassificationBadge = memo(({ name }: { name: keyof typeof classificationMapping }) => {
	return <Badge variant={classificationMapping[name]}>{name}</Badge>;
});
